<template>
  <div class="ophthalmology-item">
    <st-data-display label="Queixa principal" v-if="mainComplaint">
      <div class="text-pre" v-html="mainComplaint" />
    </st-data-display>

    <st-data-display label="História pregressa" v-if="medicalHistory">
      <div class="text-pre" v-html="medicalHistory" />
    </st-data-display>

    <div class="eye-detail-group">
      <EyeDetailCard
        v-if="visualAcuityData"
        class="avsc"
        title="Acuídade Visual"
        v-bind="visualAcuityData"
      />

      <EyeDetailCard
        v-if="tonometryData"
        class="auto-pio"
        title="Pressão Intraocular"
        v-bind="tonometryData"
      />

      <EyeDetailCard
        v-if="lensometryData"
        class="auto-ref"
        title="Lensometria"
        v-bind="lensometryData"
      />

      <EyeDetailCard
        v-if="autoRefractionData"
        class="auto-ref"
        title="Auto Refração"
        v-bind="autoRefractionData"
      />

      <EyeDetailCard
        v-if="keratometryData"
        class="auto-ref"
        title="Ceratometria"
        v-bind="keratometryData"
      />

      <EyeDetailCard
        v-if="subjectiveData"
        class="refraction"
        title="Refração Subjetiva"
        v-bind="subjectiveData"
      />

      <EyeDetailCard
        v-if="objectiveData"
        class="refraction"
        title="Refração Estática"
        v-bind="objectiveData"
      />
    </div>

    <st-data-display label="Observações dos exames" v-if="examNotes">
      <div v-for="(item, idx) in examNotes" :key="idx">
        <strong class="item-label">{{ item.label }}:</strong>
        <span>{{ item.value }}</span>
      </div>
    </st-data-display>

    <st-data-display label="Biomicroscopia" v-if="hasFilledInputWith('biomicroscopy')">
      <div class="text-pre">
        <strong class="item-label">D:</strong>
        <span>{{ getInputValue('biomicroscopyR') || '-' }}</span>
      </div>
      <div class="text-pre">
        <strong class="item-label">E:</strong>
        <span>{{ getInputValue('biomicroscopyL') || '-' }}</span>
      </div>
    </st-data-display>

    <st-data-display label="Fundo de Olho" v-if="hasFilledInputWith('fundus')">
      <div class="text-pre">
        <strong class="item-label">D:</strong>
        <span>{{ getInputValue('fundusR') || '-' }}</span>
      </div>
      <div class="text-pre">
        <strong class="item-label">E:</strong>
        <span>{{ getInputValue('fundusL') || '-' }}</span>
      </div>
    </st-data-display>

    <st-data-display label="Lente de contato" v-if="hasFilledInputWith('contactLens')">
      <div class="text-pre">
        <strong class="item-label">D:</strong>
        <span>{{ getInputValue('contactLensR') || '-' }}</span>
      </div>
      <div class="text-pre">
        <strong class="item-label">E:</strong>
        <span>{{ getInputValue('contactLensL') || '-' }}</span>
      </div>
    </st-data-display>

    <st-data-display label="Gonioscopia" v-if="hasFilledInputWith('gonioscopy')">
      <div class="text-pre">
        <strong class="item-label">D:</strong>
        <span>{{ getInputValue('gonioscopyR') || '-' }}</span>
      </div>
      <div class="text-pre">
        <strong class="item-label">E:</strong>
        <span>{{ getInputValue('gonioscopyL') || '-' }}</span>
      </div>
    </st-data-display>

    <st-data-display label="Conduta" v-if="conduct">
      <div class="text-pre" v-html="conduct" />
    </st-data-display>

    <st-data-display label="Exames complementares" v-if="complementaryExams">
      <div class="text-pre" v-html="complementaryExams" />
    </st-data-display>

    <st-data-display label="Diagnóstico" v-if="diagnosis">
      <div class="text-pre" v-html="diagnosis" />
    </st-data-display>

    <st-data-display label="CID" v-if="hasCIDs">
      <div v-for="(item, idx) in item.cids" :key="idx">
        <strong class="item-label">{{ item.code }}</strong>
        <small>-</small>
        <span class="ml-1">{{ item.name }}</span>
      </div>
    </st-data-display>

    <st-data-display label="Observações" v-if="observations">
      <div class="text-pre" v-html="observations" />
    </st-data-display>
  </div>
</template>

<script>
import { sanitize as sanitizeHTML } from '@/helpers/html';
import EyeDetailCard from '../ophtalmology/EyeDetailCard.vue';
import * as inputGroups from '../ophtalmology/inputGroups';
import { useInputs } from '../helpers/inputs';

export default {
  components: {
    EyeDetailCard,
  },
  props: {
    item: {
      type: Object,
    },
  },
  data() {
    return {
      inputs: this.item.anamnesis.inputs,
      inputHelper: useInputs(this.item.anamnesis.inputs),
    };
  },
  computed: {
    mainComplaint() {
      const value = this.getInputValue('mainComplaint');
      return value ? sanitizeHTML(value) : null;
    },
    medicalHistory() {
      const value = this.getInputValue('medicalHistory');
      return value ? sanitizeHTML(value) : null;
    },
    complementaryExams() {
      const value = this.getInputValue('complementaryExams');
      return value ? sanitizeHTML(value) : null;
    },
    conduct() {
      const value = this.getInputValue('conduct');
      return value ? sanitizeHTML(value) : null;
    },
    diagnosis() {
      const value = this.getInputValue('diagnosis');
      return value ? sanitizeHTML(value) : null;
    },
    observations() {
      const value = this.getInputValue('observations');
      return value ? sanitizeHTML(value) : null;
    },
    autoRefractionData() {
      const data = inputGroups.autoRefraction(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    keratometryData() {
      const data = inputGroups.keratometry(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    lensometryData() {
      const data = inputGroups.lensometry(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    visualAcuityData() {
      const data = inputGroups.visualAcuity(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    tonometryData() {
      const data = inputGroups.tonometry(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    subjectiveData() {
      const data = inputGroups.subjective(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    objectiveData() {
      const data = inputGroups.objective(this.inputs);
      return data.rows.length > 0 ? data : null;
    },
    examNotes() {
      const items = [];

      const noteInputs = {
        autoRefNotes: 'Auto Refração',
        keratometryNotes: 'Ceratometria',
        lensometryNotes: 'Lensometria',
        refSubjNotes: 'Refração Subjetiva',
        refObjNotes: 'Refração Estática',
      };

      Object.keys(noteInputs).forEach((name) => {
        const value = this.getInputValue(name);
        if (value) {
          items.push({
            label: noteInputs[name],
            value,
          });
        }
      });

      return items.length === 0 ? null : items;
    },
    hasCIDs() {
      return Array.isArray(this.item.cids) && this.item.cids.length > 0;
    },
  },
  methods: {
    getInputValue(name) {
      return this.inputHelper.getValue(name);
    },
    hasFilledInputWith(search) {
      return this.inputHelper.hasFilledWith(search);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.ophthalmology-item {
  .eye-detail-group {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: $layout-spacing;
    @media (max-width: $size-lg) {
      grid-template-columns: 1fr 1fr;
    }
    @media (max-width: $size-md) {
      grid-template-columns: 1fr;
    }
  }

  .eye-detail-card {
    margin-bottom: $layout-spacing;

    &.auto-ref {
      border: $border-width solid $gray-color;
    }
    &.auto-pio {
      border: $border-width solid #efcc92;
    }
    &.avsc {
      border: $border-width solid #9292c2;
    }
    &.refraction {
      border: $border-width solid #a8cba8;
    }
  }

  .item-label {
    color: $dark-blue-color-500;
    font-weight: 600;
    margin-right: $layout-spacing-sm;
  }
}
</style>
